/**
 * Figure de géométrie plane dans la leçon : la construction décrite par l'enseignant (geometry.ts), avec ses points
 * déplaçables et ses curseurs. Tout ce qui dépend d'un point déplacé (milieu, intersection, projeté, angle) suit.
 */
import { useMemo, useState } from "react";
import { Circle, Coordinates, Line, Mafs, MovablePoint, Point, Polygon, Polyline, Text, Vector } from "mafs";
import { clipLine, geoLabel, resolveGeometry, type GeoElement, type GeometrySpec, type Vec2 } from "@jange/shared";
import { C, fmt, SliderRow } from "./theme.js";

const COLOR: Record<string, string> = { teal: C.teal, amber: C.amber, ink: C.ink, muted: "#9AA3B2" };
/** Thème clair de Mafs (sombre par défaut), posé sur la figure elle-même : identique dans la leçon et dans l'éditeur. */
const GEO_CSS = `.jg-geo .MafsView { --mafs-bg: #FFFFFF; --mafs-fg: #15213B; --mafs-line-color: #DDE2EA; --grid-line-subdivision-color: #EEF0F4; --mafs-origin-color: #15213B; --mafs-orange: #E8A317; border-radius: 12px; }`;
const colorOf = (e: GeoElement, fallback: string) => (e.color ? COLOR[e.color]! : fallback);

/** Petits traits de codage (longueurs égales) au milieu d'un segment. */
function ticks(a: Vec2, b: Vec2, n: number, size: number): [Vec2, Vec2][] {
  const d: Vec2 = [b[0] - a[0], b[1] - a[1]];
  const len = Math.hypot(d[0], d[1]) || 1;
  const u: Vec2 = [d[0] / len, d[1] / len], nrm: Vec2 = [-u[1], u[0]];
  const mid: Vec2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  return Array.from({ length: n }, (_, i) => {
    const off = (i - (n - 1) / 2) * size * 0.6;
    const c: Vec2 = [mid[0] + u[0] * off, mid[1] + u[1] * off];
    return [[c[0] - nrm[0] * size, c[1] - nrm[1] * size], [c[0] + nrm[0] * size, c[1] + nrm[1] * size]];
  });
}

/** Arc d'angle (ou petit carré pour un angle droit) autour du sommet. */
export function angleShape(v: Vec2, start: number, sweep: number, r: number, right: boolean): Vec2[] {
  if (right) {
    const u1: Vec2 = [Math.cos(start) * r * 0.8, Math.sin(start) * r * 0.8];
    const u2: Vec2 = [Math.cos(start + sweep) * r * 0.8, Math.sin(start + sweep) * r * 0.8];
    return [[v[0] + u1[0], v[1] + u1[1]], [v[0] + u1[0] + u2[0], v[1] + u1[1] + u2[1]], [v[0] + u2[0], v[1] + u2[1]]];
  }
  return Array.from({ length: 25 }, (_, i) => { const t = start + (sweep * i) / 24; return [v[0] + Math.cos(t) * r, v[1] + Math.sin(t) * r] as Vec2; });
}

export function GeometryFigure({ spec, params, lang, onParam }: { spec: GeometrySpec; params: Record<string, number>; lang: string; onParam: (name: string, v: number) => void }) {
  const [moved, setMoved] = useState<Record<string, Vec2>>({});
  const items = useMemo(() => resolveGeometry(spec, params, moved), [spec, params, moved]);
  const span = Math.min(spec.view.x[1] - spec.view.x[0], spec.view.y[1] - spec.view.y[0]);
  const tick = span * 0.018, arc = span * 0.07;
  const clamp = (p: Vec2): Vec2 => [Math.max(spec.view.x[0], Math.min(spec.view.x[1], p[0])), Math.max(spec.view.y[0], Math.min(spec.view.y[1], p[1]))];
  const interactive = spec.sliders.length > 0 || spec.elements.some((e) => e.type === "point" && e.draggable);

  const nodes = spec.elements.flatMap((e) => {
    const r = items.get(e.id);
    if (!r || e.hidden) return [];
    const style = e.dashed ? "dashed" as const : "solid" as const;
    const label = geoLabel(e);
    switch (r.kind) {
      case "point": {
        if (e.type === "point" && e.draggable) return [<MovablePoint key={e.id} point={r.p} color={C.amber} onMove={(p) => setMoved((m) => ({ ...m, [e.id]: clamp(p) }))} />, ...(label ? [<Text key={`${e.id}-l`} x={r.p[0]} y={r.p[1]} attach="ne" attachDistance={14} size={15} color={C.ink}>{label}</Text>] : [])];
        return [<Point key={e.id} x={r.p[0]} y={r.p[1]} color={colorOf(e, C.ink)} />, ...(label ? [<Text key={`${e.id}-l`} x={r.p[0]} y={r.p[1]} attach="ne" attachDistance={16} size={15} color={C.ink}>{label}</Text>] : [])];
      }
      case "line": {
        const ends = clipLine(r.a, r.b, r.extent, spec.view);
        if (!ends) return [];
        const col = colorOf(e, r.extent === "segment" ? C.ink : C.teal);
        const out = [<Line.Segment key={e.id} point1={ends[0]} point2={ends[1]} color={col} style={style} weight={2.5} />];
        if (e.type === "segment" && e.marks) ticks(r.a, r.b, e.marks, tick).forEach(([p, q], i) => out.push(<Line.Segment key={`${e.id}-t${i}`} point1={p} point2={q} color={col} weight={2} />));
        if (e.type === "segment" && e.showLength) {
          const len = Math.hypot(r.b[0] - r.a[0], r.b[1] - r.a[1]);
          out.push(<Text key={`${e.id}-len`} x={(r.a[0] + r.b[0]) / 2} y={(r.a[1] + r.b[1]) / 2} attach="s" attachDistance={12} size={13} color={col}>{`${fmt(Math.round(len * 100) / 100)} ${spec.unit}`}</Text>);
        }
        if (label) { const k = 0.85; out.push(<Text key={`${e.id}-l`} x={ends[0][0] + (ends[1][0] - ends[0][0]) * k} y={ends[0][1] + (ends[1][1] - ends[0][1]) * k} attach="n" attachDistance={10} size={14} color={col}>{label}</Text>); }
        return out;
      }
      case "circle": return [<Circle key={e.id} center={r.c} radius={r.r} color={colorOf(e, C.teal)} fillOpacity={0} strokeStyle={style} weight={2.5} />];
      case "polygon": return [<Polygon key={e.id} points={r.pts} color={colorOf(e, C.ink)} fillOpacity={e.type === "polygon" && e.fill ? 0.12 : 0} strokeStyle={style} weight={2.5} />];
      case "angle": {
        const right = e.type === "angle" && !!e.right;
        const out = [<Polyline key={e.id} points={angleShape(r.v, r.start, r.sweep, arc, right)} color={colorOf(e, C.amber)} weight={2} fillOpacity={0} />];
        if (e.type === "angle" && e.showValue) {
          const mid = r.start + r.sweep / 2;
          out.push(<Text key={`${e.id}-v`} x={r.v[0] + Math.cos(mid) * arc * 2.1} y={r.v[1] + Math.sin(mid) * arc * 2.1} size={13} color={colorOf(e, C.amberText)}>{`${fmt(Math.round(r.deg * 10) / 10)}°`}</Text>);
        }
        return out;
      }
      case "vector": return [<Vector key={e.id} tail={r.a} tip={r.b} color={colorOf(e, C.teal)} weight={2.5} />];
      case "text": return [<Text key={e.id} x={r.p[0]} y={r.p[1]} size={14} color={colorOf(e, C.ink)}>{r.text}</Text>];
    }
  });

  return (
    <div className="jg-geo" style={{ border: `1px solid ${C.border}`, borderRadius: 18, padding: 12, display: "flex", flexDirection: "column", gap: 10, background: "#fff" }}>
      <style>{GEO_CSS}</style>
      {interactive && <span style={{ alignSelf: "flex-end", fontSize: 11, fontWeight: 700, color: C.teal, background: C.tealTint, padding: "4px 8px", borderRadius: 999 }}>Manipulable</span>}
      <Mafs height={280} viewBox={{ x: spec.view.x, y: spec.view.y, padding: 0 }} preserveAspectRatio="contain" pan={false} zoom={false}>
        {(spec.grid || spec.axes) && <Coordinates.Cartesian subdivisions={false} xAxis={{ axis: spec.axes, lines: spec.grid ? 1 : false, labels: spec.axes ? (v) => fmt(v) : false }} yAxis={{ axis: spec.axes, lines: spec.grid ? 1 : false, labels: spec.axes ? (v) => fmt(v) : false }} />}
        {nodes}
      </Mafs>
      {spec.sliders.map((s) => <SliderRow key={s.name} s={s} value={params[s.name] ?? s.initial} lang={lang} onChange={(v) => onParam(s.name, v)} onRelease={() => {}} />)}
    </div>
  );
}
